/**
 * MobileHomeDashboard.js - Mobile-optimized home screen
 * Provides app-like dashboard experience on mobile
 */
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Bell, MessageSquare, ArrowRight, Clock,
  Sparkles, Home as HomeIcon, Shirt, Truck, Package,
  CheckCircle, AlertCircle, MapPin, Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const serviceIcons = {
  cleaning: HomeIcon,
  laundry: Shirt,
  courier: Truck,
  errands: Package,
  tasks: Sparkles
};

const MobileHomeDashboard = ({
  user,
  bookings = [],
  notifications = [],
  unreadMessages = 0,
  services = [],
  onQuickBook,
  onViewBookings,
  onViewMessages,
  onViewNotifications,
  onSelectService
}) => {
  // Get upcoming booking
  const upcomingBooking = bookings.find(b => 
    ['pending', 'confirmed', 'assigned'].includes(b.status)
  );
  
  // Get recent activity
  const recentActivity = [...bookings]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 3);
  
  // Get unread notifications
  const unreadNotifications = notifications.filter(n => !n.read).length;

  return (
    <div className="pb-20 px-4 pt-4 space-y-6">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
      >
        <h1 className="text-2xl font-bold font-['Cormorant_Garamond']">
          Welcome back{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
        </h1>
        <p className="text-[var(--text-muted)] text-sm mt-1">
          What can we help you with today?
        </p>
      </motion.div>

      {/* Quick Actions Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-3 gap-3"
      >
        <QuickActionButton
          icon={<Calendar className="w-6 h-6" />}
          label="Bookings"
          onClick={onViewBookings}
          badge={bookings.filter(b => b.status === 'pending').length}
        />
        <QuickActionButton
          icon={<MessageSquare className="w-6 h-6" />}
          label="Messages"
          onClick={onViewMessages}
          badge={unreadMessages}
        />
        <QuickActionButton
          icon={<Bell className="w-6 h-6" />}
          label="Alerts"
          onClick={onViewNotifications}
          badge={unreadNotifications}
        />
      </motion.div>

      {/* Upcoming Booking Card */}
      {upcomingBooking && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Upcoming Booking</h2>
            <button 
              onClick={onViewBookings}
              className="text-sm text-[var(--primary)] flex items-center gap-1"
            >
              View all <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <UpcomingBookingCard booking={upcomingBooking} />
        </motion.div>
      )}

      {/* Quick Book Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Quick Book</h2>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 mobile-scroll-x">
          {services.slice(0, 5).map((service) => {
            const Icon = serviceIcons[service.category] || Sparkles;
            return (
              <button
                key={service.id}
                onClick={() => onSelectService(service)}
                className="flex-shrink-0 w-24 flex flex-col items-center p-4 rounded-2xl bg-[var(--background-paper)] border border-[var(--border-primary)] hover:border-[var(--primary)] transition-all active:scale-95"
              >
                <div className="w-12 h-12 rounded-full bg-[var(--primary)]/20 flex items-center justify-center mb-2">
                  <Icon className="w-6 h-6 text-[var(--primary)]" />
                </div>
                <span className="text-xs font-medium text-center leading-tight">
                  {service.name.length > 12 ? service.name.slice(0, 12) + '...' : service.name}
                </span>
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* CTA Banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] p-5">
          <div className="relative z-10">
            <h3 className="text-black font-bold text-lg mb-1">
              Need help today?
            </h3>
            <p className="text-black/70 text-sm mb-4">
              Book a service in minutes
            </p>
            <Button
              onClick={onQuickBook}
              className="bg-black text-white hover:bg-black/80"
            >
              Book Now
            </Button>
          </div>
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
      </motion.div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="text-lg font-semibold mb-3">Recent Activity</h2>
          <div className="space-y-3">
            {recentActivity.map((booking) => (
              <RecentActivityCard key={booking.id} booking={booking} />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Quick Action Button Component
const QuickActionButton = ({ icon, label, onClick, badge = 0 }) => (
  <button
    onClick={onClick}
    className="relative flex flex-col items-center justify-center p-4 rounded-2xl bg-[var(--background-paper)] border border-[var(--border-primary)] hover:border-[var(--primary)] transition-all active:scale-95 min-h-[80px]"
  >
    <div className="text-[var(--primary)] mb-1">{icon}</div>
    <span className="text-xs font-medium">{label}</span>
    {badge > 0 && (
      <span className="absolute top-2 right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
        {badge > 9 ? '9+' : badge}
      </span>
    )}
  </button>
);

// Upcoming Booking Card
const UpcomingBookingCard = ({ booking }) => {
  const Icon = serviceIcons[booking.service_type] || Sparkles;
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-500',
    confirmed: 'bg-blue-500/20 text-blue-500',
    assigned: 'bg-purple-500/20 text-purple-500',
    in_progress: 'bg-green-500/20 text-green-500'
  };
  
  return (
    <div className="p-4 rounded-2xl bg-[var(--background-paper)] border border-[var(--border-gold)]">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-[var(--primary)]/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-7 h-7 text-[var(--primary)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-semibold truncate">{booking.service_name}</h3>
            <Badge className={statusColors[booking.status] || 'bg-gray-500/20 text-gray-500'}>
              {booking.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
            <Calendar className="w-4 h-4" />
            <span>
              {new Date(booking.scheduled_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
            </span>
            <Clock className="w-4 h-4 ml-2" />
            <span>{booking.scheduled_time}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] mt-1">
            <MapPin className="w-4 h-4" />
            <span className="truncate">{booking.address}</span>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-[var(--border-primary)] flex items-center justify-between">
        <span className="text-lg font-bold text-[var(--primary)]">
          ${booking.total_amount?.toFixed(2) || '0.00'}
        </span>
        <Button size="sm" className="bg-[var(--primary)] text-black hover:bg-[var(--primary-hover)]">
          View Details
        </Button>
      </div>
    </div>
  );
};

// Recent Activity Card
const RecentActivityCard = ({ booking }) => {
  const statusIcons = {
    completed: <CheckCircle className="w-5 h-5 text-green-500" />,
    cancelled: <AlertCircle className="w-5 h-5 text-red-500" />,
    pending: <Clock className="w-5 h-5 text-yellow-500" />,
    confirmed: <CheckCircle className="w-5 h-5 text-blue-500" />
  };
  
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-[var(--background-paper)] border border-[var(--border-primary)]">
      <div className="w-10 h-10 rounded-full bg-[var(--background-subtle)] flex items-center justify-center">
        {statusIcons[booking.status] || <Calendar className="w-5 h-5 text-[var(--text-muted)]" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{booking.service_name}</p>
        <p className="text-xs text-[var(--text-muted)]">
          {new Date(booking.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
        </p>
      </div>
      <span className="text-sm font-medium capitalize">{booking.status}</span>
    </div>
  );
};

export default MobileHomeDashboard;
